import React from 'react';

import * as Sentry from '@sentry/react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Flex,
  Spinner,
  useToastContext,
} from '@resideo/blueprint-react';

import ModalHeader from 'components/common/ModalHeader';
import PageLoading from 'components/common/PageLoading';
import Typography from 'components/common/Typography';

import { useConnectedDevices } from 'hooks/useDataStream';
import { useRisClient } from 'hooks/useRisClient';

import {
  AddIcon,
  DeviceEntryContainer,
  RemoveIcon,
  ScrollableArea,
} from './DataStreamScreen.style';

import {
  PartnerCloudAccount,
  PartnerCloudConnectedDevice,
  PartnerCloudDeviceRemoveRequest,
  PartnerCloudDisconnectRequest,
  RisError,
} from 'types/types';

interface ModalProps {
  account: PartnerCloudAccount;

  openSignInModal: (account: PartnerCloudAccount) => void;
  closeModal: () => void;
}

const { Text } = Typography;

const DeviceEntry = ({
  account,
  device,
  closeModal,
}: {
  account: PartnerCloudAccount;
  device: PartnerCloudConnectedDevice;
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();

  const { client } = useRisClient();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const removeDevice = useMutation({
    mutationKey: ['dataStream', 'removeDevice', device.deviceId],
    mutationFn: async ({
      partnerCloudId,
      deviceId,
    }: PartnerCloudDeviceRemoveRequest) =>
      await client.partnerClouds.myUsersDeletePartnerCloudDataStreamingDevice(
        partnerCloudId,
        deviceId
      ),

    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: ['dataStream', 'accounts'] });

      const previousAccounts = queryClient.getQueryData([
        'dataStream',
        'accounts',
      ]);

      const previousDevices = queryClient.getQueryData([
        'dataStream',
        'connectedDevices',
        variables.partnerCloudId,
      ]);

      return { previousAccounts, previousDevices };
    },

    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ['dataStream', 'accounts'],
        (old: PartnerCloudAccount[]) => {
          return old.map(oldAccountData => {
            if (oldAccountData.partnerCloudId === variables.partnerCloudId) {
              const updatedConnectedDevices =
                oldAccountData.connectedDevices.filter(
                  d => d.deviceId !== variables.deviceId
                );

              if (!updatedConnectedDevices.length) {
                closeModal();
              }

              return {
                ...oldAccountData,
                connectedDevices: updatedConnectedDevices,
              };
            }

            return oldAccountData;
          });
        }
      );

      queryClient.setQueryData(
        ['dataStream', 'connectedDevices', variables.partnerCloudId],
        (old: PartnerCloudConnectedDevice[]) => {
          return old.filter(d => d.deviceId !== variables.deviceId);
        }
      );
    },

    onError: (error: RisError, variables, context) => {
      queryClient.setQueryData(
        ['dataStream', 'accounts'],
        context?.previousAccounts
      );

      queryClient.setQueryData(
        ['dataStream', 'connectedDevices', variables.partnerCloudId],
        context?.previousDevices
      );

      addToast({
        toastType: 'Error',
        message: t(
          'privacy.dataStream.accountSettings.errors.removeDeviceFailed'
        ),
      });

      Sentry.captureEvent({
        message: '[Data Streaming] Error removing partner device',
        level: 'error',
        extra: { variables },
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'devices', account.partnerCloudId],
        refetchType: 'none',
      });

      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'connectedDevices', account.partnerCloudId],
        refetchType: 'none',
      });
    },

    retry: false,
  });

  const handleRemoveDevice = (d: PartnerCloudConnectedDevice) => {
    removeDevice.mutate({
      partnerCloudId: account.partnerCloudId,
      deviceId: d.deviceId,
    });
  };

  return (
    <DeviceEntryContainer
      data-test='data-stream-device-list-entry'
      alignItems='center'
      justifyContent='space-between'>
      <Flex flexDirection='column'>
        <Text strong='true' marginBottom='8px'>
          {device.deviceName}
        </Text>

        <Text marginBottom='8px'>{device.deviceModel}</Text>

        <Text marginBottom='8px'>{device.deviceType}</Text>

        <Text>{device.deviceId}</Text>
      </Flex>

      <Button
        data-test='data-stream-account-settings-remove-device'
        type='button'
        size='small'
        variant='destructiveTertiary'
        style={{ minWidth: 0, padding: 0 }}
        onClick={() => handleRemoveDevice(device)}>
        {removeDevice.isPending ? (
          <Spinner
            aria-label={t('common.removingDevice')}
            marginX='2px'
            size='large'
            verticalAlign='text-top'
          />
        ) : (
          <RemoveIcon style={{ paddingTop: '8px' }} />
        )}
      </Button>
    </DeviceEntryContainer>
  );
};

const SettingsModal = ({
  account,
  openSignInModal,
  closeModal,
}: ModalProps) => {
  const queryClient = useQueryClient();

  const { client } = useRisClient();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const { data: connectedDevices, isFetching } = useConnectedDevices(
    account.partnerCloudId
  );

  const disconnectAccount = useMutation({
    mutationKey: ['dataStream', 'disconnectAccount'],
    mutationFn: async ({
      partnerCloudId,
      devices,
    }: PartnerCloudDisconnectRequest) => {
      devices.forEach(
        async device =>
          await client.partnerClouds.myUsersDeletePartnerCloudDataStreamingDevice(
            partnerCloudId,
            device.deviceId
          )
      );
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['dataStream', 'accounts'] });

      const previousAccounts = queryClient.getQueryData([
        'dataStream',
        'accounts',
      ]);

      return { previousAccounts };
    },

    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ['dataStream', 'accounts'],
        (old: PartnerCloudAccount[]) => {
          return old.map(oldAccountData => {
            if (oldAccountData.partnerCloudId === variables.partnerCloudId) {
              return {
                ...oldAccountData,
                connectedDevices: [],
              };
            }

            return oldAccountData;
          });
        }
      );

      closeModal();
    },

    onError: (error: RisError, variables, context) => {
      addToast({
        toastType: 'Error',
        message: t(
          'privacy.dataStream.accountSettings.errors.disconnectFailed'
        ),
      });

      queryClient.setQueryData(
        ['dataStream', 'accounts'],
        context?.previousAccounts
      );

      Sentry.captureEvent({
        message: '[Data Streaming] Error disconnecting partner account',
        level: 'error',
        extra: { variables },
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'devices', account.partnerCloudId],
        refetchType: 'none',
      });

      queryClient.invalidateQueries({
        queryKey: ['dataStream', 'connectedDevices', account.partnerCloudId],
        refetchType: 'none',
      });
    },

    retry: false,
  });

  const handleDisconnectAccount = () => {
    if (!connectedDevices?.length) return;

    disconnectAccount.mutate({
      partnerCloudId: account.partnerCloudId,
      devices: connectedDevices,
    });
  };

  return (
    <>
      <ModalHeader
        title={t('privacy.dataStream.accountSettings.title', {
          accountName: account.partnerCloudName,
        })}
        closeModal={closeModal}
      />

      <Box>
        <Text>{t('privacy.dataStream.accountSettings.description')}</Text>

        {isFetching ? (
          <PageLoading />
        ) : (
          <ScrollableArea>
            {connectedDevices?.map(device => (
              <DeviceEntry
                key={device.deviceId}
                account={account}
                device={device}
                closeModal={closeModal}
              />
            ))}
          </ScrollableArea>
        )}

        <Button
          data-test='data-stream-account-settings-add-devices'
          type='button'
          variant='tertiary'
          style={{ display: 'flex', alignItems: 'center' }}
          onClick={() => {
            openSignInModal(account);
          }}>
          <AddIcon />
          {t('privacy.dataStream.accountSettings.add')}
        </Button>
      </Box>

      <Flex
        data-test='data-stream-account-settings-actions'
        justifyContent='flex-end'
        paddingTop='large'>
        <Button
          disabled={!connectedDevices?.length}
          variant='destructivePrimary'
          marginX='medium'
          onClick={handleDisconnectAccount}>
          {disconnectAccount.isPending ? (
            <Spinner
              aria-label={t('common.disconnectingAccount')}
              color='white'
            />
          ) : (
            t('common.disconnectAccount')
          )}
        </Button>

        <Button onClick={closeModal}>{t('common.close')}</Button>
      </Flex>
    </>
  );
};

export default SettingsModal;
